<script setup lang="ts">
import { ref, reactive, computed, inject, onMounted, nextTick } from 'vue';
import type { FormSchema, FormSectionDef } from '@fondationcss/sdk';
import { FormFieldType } from '@fondationcss/sdk';
import { allFields } from '@fondationcss/forms';
import { getCustomSdk } from '@/sdk';
import SimpleMarkdown from '@/components/legal/simple-markdown.vue';
import { ADMISSION_CONSENT_NOTICE, ADMISSION_CONSENT_NOTICE_VERSION, hashNoticeText } from '@/config/admission-consent';
import AdmissionPage from './admission-page.vue';

const sdk = getCustomSdk();

// En mode « embedded », le composant est rendu dans les 3/4 droite de l'écran de
// connexion (le col image de gauche appartient alors à la Fondation) : pas de
// panneau image propre, un logo en tête et un bouton de retour.
const props = defineProps<{ embedded?: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();

// Image d'accueil (réutilise celle de l'écran de connexion : /images/<prefix>-bg{1,2,3}.webp).
const appConfig = inject<{ DefaultLayout?: string; SiteLogo?: string; SiteLogoAlt?: string }>('config');
const bgImage = (() => {
  const images = [1, 2, 3].map((n) => `/images/bg${n}.webp`);
  return images[Math.floor(Math.random() * images.length)];
})();
// Animation d'entrée : le panneau gauche s'ouvre (largeur → 25%) puis la page apparaît.
const imageOpen = ref(false);
const contentVisible = ref(false);

// Paramètres configurables (admin) : logo + instructions du formulaire.
const instructions = ref('');
const logoUrl = ref('');
// Logo clair (sur image sombre, panneau gauche de la route) et logo sombre (en
// tête du formulaire sur fond blanc, mode embedded).
const displayLogo = computed(() => logoUrl.value || appConfig?.SiteLogoAlt || appConfig?.SiteLogo || '/perspective-logo-white.svg');
const displayLogoDark = computed(() => logoUrl.value || appConfig?.SiteLogo || '/perspective-logo.svg');

async function loadSettings() {
  try {
    const s = await sdk.getPublicAdmissionSettings();
    instructions.value = s.instructionsMarkdown ?? '';
    logoUrl.value = s.logoUrl ?? '';
  } catch {
    /* valeurs par défaut si indisponible */
  }
}

// --- Authentification / accueil ---
const loadingMine = ref(true);
const authenticated = ref(false);
const confirmedEmail = ref<string | null>(null);
const lockedStatus = ref<string | null>(null); // demande déjà en traitement (non éditable)

// --- Consentement Loi 25 ---
const consentChecked = ref(false);
const consentGiven = ref(false);
// Source unique de vérité : l'avis de collecte publié dans Core.Privacy (Fondation).
// On retombe sur l'avis intégré au build tant qu'aucun avis n'est publié, pour ne
// jamais bloquer le formulaire public.
const noticeMarkdown = ref(ADMISSION_CONSENT_NOTICE);
// Identifiant du document juridique réellement affiché. Null tant qu'aucun avis n'est publié :
// le consentement est alors enregistré sans lien de version, plutôt qu'avec une chaîne libre
// qui ne renverrait à aucun document existant (#115).
const noticeDocumentId = ref<string | null>(null);
const noticeLoaded = ref(false);
// Sans avis publié, la Fondation exige comme preuve l'empreinte SHA-256 du texte intégré affiché.
let noticeHash: Promise<string | null> = Promise.resolve(null);
const acceptedNoticeDocumentId = ref<string | null>(null);
let acceptedNoticeHash: Promise<string | null> = Promise.resolve(null);

async function loadNotice() {
  try {
    const doc = await sdk.getPublicPrivacyNotice();
    if (doc) {
      noticeMarkdown.value = doc.bodyMarkdown;
      noticeDocumentId.value = doc.id;
    }
  } catch {
    /* on conserve l'avis intégré au build */
  } finally {
    // Started now, awaited only on submit: hashing must not hold the notice back.
    if (!noticeDocumentId.value) noticeHash = hashNoticeText(noticeMarkdown.value).catch(() => null);
    noticeLoaded.value = true;
  }
}

// --- Formulaire ---
const loading = ref(true);
const saving = ref(false);
const error = ref<string | null>(null);
const submitted = ref(false);
const schema = ref<FormSchema>({ pages: [] });
const values = reactive<Record<string, unknown>>({});
const step = ref(1);
// Un <v-form> par page du stepper : les pages visitées restent montées (cachées) et
// leurs champs enregistrés — un seul formulaire global validerait donc des champs
// invisibles sans pouvoir dire à l'utilisateur où retourner (#161).
type PageForm = { validate: () => Promise<{ valid: boolean }> };
const pageForms = ref<(PageForm | null)[]>([]);
function setPageForm(pi: number, el: unknown) {
  pageForms.value[pi] = el ? (el as PageForm) : null;
}
const errorAlert = ref<{ $el?: HTMLElement } | null>(null);
function showError(msg: string) {
  error.value = msg;
  nextTick(() => errorAlert.value?.$el?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
}

// Mobile number is collected unverified: SMS confirmation is removed until liano is integrated (#169).
const phone = ref('');

const pages = computed(() => [...schema.value.pages].sort((a, b) => a.order - b.order));
const multiPage = computed(() => pages.value.length > 1);

const sortedSections = (s: FormSectionDef[]) => [...s].sort((a, b) => a.order - b.order);
const sortedRows = (s: FormSectionDef) => [...s.rows].sort((a, b) => a.order - b.order);

async function loadSchema(existingValues: Record<string, unknown>) {
  loading.value = true;
  try {
    schema.value = await sdk.getPublicAdmissionSchema();
    for (const f of allFields(schema.value)) {
      values[f.key] = existingValues[f.key] ?? values[f.key] ?? null;
    }
    if (confirmedEmail.value) values['email'] = confirmedEmail.value; // courriel confirmé
  } catch {
    error.value = "Impossible de charger le formulaire d'admission.";
  } finally {
    loading.value = false;
  }
}

async function loadMine() {
  loadingMine.value = true;
  try {
    const mine = await sdk.getMyAdmission();
    authenticated.value = mine.email !== null;
    confirmedEmail.value = mine.email;
    if (authenticated.value) {
      phone.value = mine.cellPhoneNumber ?? '';
      if (mine.admissionId && mine.editable === false) {
        lockedStatus.value = mine.status ?? 'EnTraitement';
      }
      await loadSchema((mine.values as Record<string, unknown>) ?? {});
      // A resumed editable dossier requires acceptance of the notice currently displayed.
      // The previous consent is historical evidence, not acceptance of this submission.
    }
  } catch {
    authenticated.value = false;
  } finally {
    loadingMine.value = false;
  }
}

function startLogin() {
  sdk.startCourierIdInscription();
}
function acceptConsent() {
  if (!noticeLoaded.value || !consentChecked.value) return;
  // Freeze the version the candidate actually read, rather than a later response.
  acceptedNoticeDocumentId.value = noticeDocumentId.value;
  acceptedNoticeHash = noticeHash;
  consentGiven.value = true;
}

const CUSTOM_REQUIRED = new Set<FormFieldType>([
  FormFieldType.RichText, FormFieldType.CheckboxGroup, FormFieldType.FileUpload,
  FormFieldType.ImageUpload, FormFieldType.Signature, FormFieldType.Rating,
  FormFieldType.Color, FormFieldType.Address, FormFieldType.GeoCoordinates, FormFieldType.Matrix,
]);
function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v as object).length === 0;
  return false;
}

// Valide une page : règles Vuetify des champs montés, puis champs obligatoires des
// types « custom » (dont le widget ne porte pas de règle Vuetify). Retourne le message
// d'erreur, ou null si la page est valide.
async function validatePage(pi: number): Promise<string | null> {
  const page = pages.value[pi];
  if (!page) return null;
  const form = pageForms.value[pi];
  const res = await form?.validate?.();
  if (res && res.valid === false) return 'Veuillez corriger les champs en rouge.';
  // Page jamais montée (form absent) : aucun champ enregistré côté Vuetify, on vérifie
  // alors tous les obligatoires manuellement.
  const fields = sortedSections(page.sections).flatMap((s) => sortedRows(s).flatMap((r) => r.fields));
  const missing = fields.filter(
    (f) => f.required && !f.hidden && f.type !== FormFieldType.Hidden && f.type !== FormFieldType.Separator
      && (form ? CUSTOM_REQUIRED.has(f.type) : true) && isEmpty(values[f.key]),
  );
  if (missing.length) return 'Champs obligatoires manquants : ' + missing.map((f) => f.label).join(', ');
  return null;
}

async function nextStep() {
  const msg = await validatePage(step.value - 1);
  if (msg) {
    showError(msg);
    return;
  }
  error.value = null;
  step.value++;
}

function prevStep() {
  error.value = null;
  step.value--;
}

async function submit() {
  if (!consentGiven.value || !noticeLoaded.value || lockedStatus.value || saving.value) return;
  saving.value = true;
  error.value = null;
  try {
    // Valide page par page pour ramener l'utilisateur sur la première page fautive
    // plutôt que d'échouer sur un champ invisible (#161).
    for (let i = 0; i < pages.value.length; i++) {
      const msg = await validatePage(i);
      if (msg) {
        if (multiPage.value) step.value = i + 1;
        showError(msg);
        return;
      }
    }
    await sdk.submitPublicAdmission({
      values: { ...values },
      consentAccepted: true,
      ...(acceptedNoticeDocumentId.value
        ? { consentLegalDocumentId: acceptedNoticeDocumentId.value }
        : {
            consentEmbeddedNoticeVersion: ADMISSION_CONSENT_NOTICE_VERSION,
            consentEmbeddedNoticeHash: (await acceptedNoticeHash) ?? undefined,
          }),
      phoneNumber: phone.value.trim() || undefined,
    });
    submitted.value = true;
  } catch {
    showError("L'envoi a échoué. Veuillez réessayer.");
  } finally {
    saving.value = false;
  }
}

onMounted(() => {
  loadSettings();
  loadNotice();
  loadMine();
  // Mode embedded : la transition de largeur est gérée par la Fondation (col image
  // qui rétrécit), on affiche simplement le contenu. Route autonome : on ouvre le
  // panneau image (largeur → 25%) puis on révèle le formulaire.
  const isNarrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 959px)').matches;
  if (props.embedded || isNarrow) {
    contentVisible.value = true;
    return;
  }
  requestAnimationFrame(() => {
    imageOpen.value = true;
    contentVisible.value = true;
  });
});
</script>

<template>
  <div class="admission-layout" :class="{ 'is-embedded': props.embedded }">
    <button
      v-if="props.embedded"
      type="button"
      class="admission-close"
      aria-label="Retour à la connexion"
      @click="emit('close')"
    >
      <v-icon size="20">fas fa-arrow-left</v-icon>
    </button>
    <div
      v-if="!props.embedded"
      class="admission-image"
      :class="{ 'is-open': imageOpen }"
      :style="{ backgroundImage: `url(${bgImage})` }"
    >
      <div class="admission-image-overlay">
        <img :src="displayLogo" alt="Logo" class="admission-logo" />
      </div>
    </div>
    <div class="admission-body" :class="{ 'is-visible': contentVisible }">
  <v-container class="admission-public py-8" style="max-width: 860px; width: 100%;">
    <!-- Mode embedded : logo (configurable) en tête du formulaire. -->
    <div v-if="props.embedded" class="admission-embedded-header">
      <img :src="displayLogoDark" alt="Logo" class="admission-embedded-logo" />
    </div>
    <div v-if="loadingMine || (authenticated && !consentGiven && !noticeLoaded)" class="d-flex justify-center pa-8"><v-progress-circular indeterminate /></div>

    <!-- Non authentifié : accueil + connexion CourierID -->
    <v-card v-else-if="!authenticated" class="pa-6 text-center" rounded="lg">
      <v-icon size="48" color="primary" class="mb-3">fas fa-user-plus</v-icon>
      <h1 class="text-h5 mb-2">Demande d'admission</h1>
      <p class="text-body-1 mb-4">
        Pour débuter votre demande, connectez-vous. Votre courriel sera confirmé automatiquement.
      </p>
      <v-btn color="primary" size="large" @click="startLogin">
        <v-icon start>fas fa-right-to-bracket</v-icon>Commencer mon inscription
      </v-btn>
    </v-card>

    <!-- Demande déjà en traitement (non éditable) -->
    <v-card v-else-if="lockedStatus" class="pa-6 text-center" rounded="lg">
      <v-icon size="48" color="teal" class="mb-3">fas fa-hourglass-half</v-icon>
      <h2 class="text-h5 mb-2">Demande en traitement</h2>
      <p class="text-body-1">
        Votre demande a été reçue et est en cours de traitement. Notre équipe communiquera
        avec vous par courriel. Vous ne pouvez plus la modifier pour le moment.
      </p>
    </v-card>

    <!-- Confirmation d'envoi -->
    <v-card v-else-if="submitted" class="pa-6 text-center" rounded="lg">
      <v-icon size="56" color="success" class="mb-3">fas fa-circle-check</v-icon>
      <h2 class="text-h5 mb-2">Demande envoyée</h2>
      <p class="text-body-1">
        Merci. Votre demande d'admission a bien été reçue. Vous pouvez la modifier tant
        qu'elle n'a pas été traitée, en revenant sur cette page.
      </p>
    </v-card>

    <!-- Consentement Loi 25 (nouvelle demande) -->
    <v-card v-else-if="!consentGiven" class="pa-6" rounded="lg">
      <h1 class="text-h5 mb-1">Demande d'admission</h1>
      <p class="text-medium-emphasis mb-4">
        Avant de commencer, veuillez prendre connaissance de l'avis de confidentialité.
      </p>
      <v-divider class="mb-4" />
      <div style="max-height: 55vh; overflow-y: auto;">
        <SimpleMarkdown :source="noticeMarkdown" />
      </div>
      <v-divider class="my-4" />
      <v-checkbox v-model="consentChecked" density="compact" hide-details label="J'ai lu et j'accepte" />
      <div class="d-flex justify-end mt-4">
        <v-btn color="primary" :disabled="!consentChecked" @click="acceptConsent">Continuer</v-btn>
      </div>
    </v-card>

    <!-- Formulaire -->
    <v-card v-else class="pa-6 admission-form-card" rounded="lg">
      <div v-if="loading" class="d-flex justify-center pa-8"><v-progress-circular indeterminate /></div>
      <template v-else>
        <h1 class="text-h5 mb-1">Demande d'admission</h1>
        <p v-if="confirmedEmail" class="text-medium-emphasis mb-2">
          Connecté en tant que <strong>{{ confirmedEmail }}</strong> (courriel confirmé)
        </p>
        <!-- Instructions configurables (admin) -->
        <div v-if="instructions" class="admission-instructions mb-4">
          <SimpleMarkdown :source="instructions" />
        </div>
        <v-alert
          v-if="error"
          ref="errorAlert"
          type="error"
          variant="text"
          density="compact"
          class="admission-error-alert mb-3"
        >
          {{ error }}
        </v-alert>

        <v-stepper v-if="multiPage" v-model="step" :items="pages.map(p => p.title)" hide-actions flat>
          <template v-for="(page, pi) in pages" #[`item.${pi+1}`] :key="page.id">
            <v-form :ref="(el: unknown) => setPageForm(pi, el)" @submit.prevent>
              <AdmissionPage :page="page" :values="values" :confirmed-email="confirmedEmail"
                :show-phone="pi === 0" v-model:phone="phone"
                @update-field="(key, value) => values[key] = value" />
            </v-form>
          </template>
        </v-stepper>
        <v-form v-else-if="pages[0]" :ref="(el: unknown) => setPageForm(0, el)" @submit.prevent>
          <AdmissionPage :page="pages[0]" :values="values" :confirmed-email="confirmedEmail"
            show-phone v-model:phone="phone" @update-field="(key, value) => values[key] = value" />
        </v-form>

        <div class="d-flex justify-space-between mt-4">
          <div>
            <v-btn v-if="multiPage && step > 1" variant="text" @click="prevStep">
              <v-icon start>fas fa-arrow-left</v-icon>Précédent
            </v-btn>
          </div>
          <div class="d-flex ga-2">
            <v-btn
              v-if="multiPage && step < pages.length"
              color="primary"
              @click="nextStep"
            >
              Suivant<v-icon end>fas fa-arrow-right</v-icon>
            </v-btn>
            <v-btn v-if="!multiPage || step === pages.length" color="primary" :loading="saving" @click="submit">
              Envoyer ma demande
            </v-btn>
          </div>
        </div>
      </template>
    </v-card>
  </v-container>
    </div>
  </div>
</template>

<style scoped>
.admission-layout {
  display: flex;
  min-height: 100vh;
  width: 100%;
}
.admission-close {
  position: absolute;
  top: 16px;
  left: 20px;
  z-index: 10;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: background 0.15s ease;
}
.admission-close:hover {
  background: #fff;
}

/* Panneau gauche (grands écrans) : s'ouvre en largeur jusqu'à 25% et porte le logo. */
.admission-image {
  position: relative;
  flex: 0 0 0;
  width: 0;
  align-self: stretch;
  overflow: hidden;
  /* Repli couleur de thème : garantit un logo (blanc) lisible même si l'image ne charge pas. */
  background-color: #3460a8;
  background-size: cover;
  background-position: center;
  transition: flex-basis 0.65s cubic-bezier(0.22, 1, 0.36, 1),
              width 0.65s cubic-bezier(0.22, 1, 0.36, 1);
}
.admission-image.is-open {
  flex-basis: 25%;
  width: 25%;
  max-width: 25%;
}

/* Mode embedded (rendu dans les 3/4 droite du login) : plein espace, logo en tête. */
.admission-layout.is-embedded {
  position: relative;
  min-height: 100%;
}
.admission-embedded-header {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}
.admission-embedded-logo {
  max-height: 64px;
  max-width: 60%;
  object-fit: contain;
}
.admission-image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(180deg, rgba(20, 30, 55, 0.35), rgba(20, 30, 55, 0.55));
}
.admission-logo {
  max-width: 78%;
  max-height: 120px;
  object-fit: contain;
}

.admission-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  opacity: 0;
  transform: translateY(10px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}
.admission-body.is-visible {
  opacity: 1;
  transform: none;
}

/* Le formulaire occupe la pleine hauteur de la fenêtre. */
.admission-form-card {
  min-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}
.admission-instructions {
  background: rgba(52, 96, 168, 0.06);
  border-left: 3px solid rgba(52, 96, 168, 0.55);
  border-radius: 6px;
  padding: 12px 16px;
}

/* Vuetify alerts grow by default, which stretches this alert inside the full-height flex card. */
.admission-error-alert {
  flex: none;
}

/* Étape sélectionnée ou complétée du stepper : cercle en couleur primaire du thème —
   le défaut Vuetify (surface-variant) rend un cercle noir. */
.admission-form-card :deep(.v-stepper-item--selected .v-stepper-item__avatar.v-avatar),
.admission-form-card :deep(.v-stepper-item--complete .v-stepper-item__avatar.v-avatar) {
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-on-primary));
}

/* Mobile (iPhone / Android) : pas d'image, formulaire pleine largeur. */
@media (max-width: 959px) {
  .admission-image,
  .admission-image.is-open {
    display: none;
    flex-basis: 0;
    width: 0;
  }
  .admission-body {
    /* pas d'attente d'animation d'image sur mobile */
    transition: opacity 0.4s ease;
    transform: none;
  }
}

/* Respect des préférences de réduction de mouvement. */
@media (prefers-reduced-motion: reduce) {
  .admission-image,
  .admission-body {
    transition: none;
  }
}
</style>
